import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useToolRoleAdmin } from "../hooks/useToolRoleAdmin";

const UserAssignedPagesContext = createContext(null);

/**
 * Shares a single fetch of the current user's Tool-Role assigned pages/features
 * across every consumer (NavHeader, RequirePageAccess, page components, ...)
 * instead of each one calling the API independently.
 */
export const UserAssignedPagesProvider = ({ children }) => {
  const { fetchUserAssignedPages } = useToolRoleAdmin();
  const [pageKeys, setPageKeys] = useState([]);
  const [features, setFeatures] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | loaded | error
  const inFlightRef = useRef(null);

  const ensureLoaded = useCallback(
    (force = false) => {
      if (!force && inFlightRef.current) return inFlightRef.current;
      if (!force && status === "loaded") return Promise.resolve({ pageKeys, features });

      setStatus("loading");
      const request = fetchUserAssignedPages()
        .then((json) => {
          const keys = (json?.data || []).map((p) => p.key);
          const feats = json?.features || {};
          setPageKeys(keys);
          setFeatures(feats);
          setStatus("loaded");
          return { pageKeys: keys, features: feats };
        })
        .catch((err) => {
          setPageKeys([]);
          setFeatures({});
          setStatus("error");
          throw err;
        })
        .finally(() => {
          inFlightRef.current = null;
        });

      inFlightRef.current = request;
      return request;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchUserAssignedPages, status]
  );

  const value = useMemo(
    () => ({ pageKeys, features, status, ensureLoaded }),
    [pageKeys, features, status, ensureLoaded]
  );

  return <UserAssignedPagesContext.Provider value={value}>{children}</UserAssignedPagesContext.Provider>;
};

export const useUserAssignedPages = () => {
  const ctx = useContext(UserAssignedPagesContext);
  if (!ctx) {
    throw new Error("useUserAssignedPages must be used within a UserAssignedPagesProvider");
  }
  return ctx;
};

export default UserAssignedPagesContext;
